import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { http } from "../../lib/api";

const inputCls =
  "w-full border border-[#111111]/15 bg-white px-3.5 py-3 text-sm focus:border-[#0F4C81] focus:outline-none";

const isSimple = (v) => typeof v === "string" || typeof v === "number" || typeof v === "boolean";

export const ContentPanel = ({ collection, label, newTemplate }) => {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    http
      .get(`/content/${collection}`)
      .then((r) => {
        setDocs(r.data);
        if (r.data.length && !active) {
          setActive(r.data[0].slug);
          setDraft(r.data[0]);
        }
      })
      .catch(() => toast.error(`Could not load ${label}.`));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const select = (doc) => {
    setActive(doc.slug);
    setDraft(doc);
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.put(`/admin/${collection}/${draft.slug}`, draft);
      toast.success("Saved. Live on the website immediately.");
      load();
    } catch (e) {
      toast.error("Could not save. Check the JSON fields.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    await http.delete(`/admin/${collection}/${slug}`);
    setActive(null);
    setDraft(null);
    toast.success("Deleted");
    load();
  };

  const addNew = () => {
    const slug = `new-${label.toLowerCase()}-${Date.now().toString().slice(-5)}`;
    const doc = { ...newTemplate, slug };
    setDocs((d) => [...d, doc]);
    setActive(slug);
    setDraft(doc);
  };

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div className="grid gap-8 lg:grid-cols-12" data-testid={`admin-${collection}`}>
      <div className="lg:col-span-4">
        <div className="flex items-center justify-between">
          <p className="mako-label text-[#7A7F85]">{label}</p>
          {newTemplate && (
            <button
              onClick={addNew}
              data-testid={`${collection}-add`}
              className="inline-flex items-center gap-2 text-[0.625rem] font-bold uppercase tracking-[0.14em] text-[#0F4C81]"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          )}
        </div>
        <ul className="mt-5 divide-y divide-[#111111]/10 border border-[#111111]/10 bg-white">
          {docs.map((d) => (
            <li key={d.slug}>
              <button
                onClick={() => select(d)}
                data-testid={`${collection}-item-${d.slug}`}
                className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-sm transition-colors duration-200 ${
                  active === d.slug ? "bg-[#F5F5F2] text-[#0F4C81]" : "text-[#111111] hover:bg-[#F5F5F2]"
                }`}
              >
                <span className="font-semibold">{d.name || d.title || d.facility || d.slug}</span>
                <span className="text-[0.625rem] uppercase tracking-wider text-[#7A7F85]">
                  {d.slug}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="lg:col-span-8">
        {draft ? (
          <div className="border border-[#111111]/10 bg-white p-7 lg:p-9">
            <div className="flex items-center justify-between gap-5">
              <p className="font-display text-lg font-bold uppercase tracking-tight text-[#111111]">
                {draft.name || draft.title || draft.facility || draft.slug}
              </p>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => remove(draft.slug)}
                  className="text-[#7A7F85] hover:text-[#b3261e]"
                  aria-label="Delete"
                  data-testid={`${collection}-delete`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid={`${collection}-save`}
                  className="inline-flex items-center gap-2 bg-[#0F4C81] px-6 py-3 text-[0.625rem] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#1463A5] disabled:opacity-60"
                >
                  <Save className="h-3.5 w-3.5" />
                  {saving ? "Saving" : "Save"}
                </button>
              </div>
            </div>

            <div className="mt-8 space-y-6">
              {Object.entries(draft)
                .filter(([k]) => k !== "slug")
                .map(([key, value]) => (
                  <label key={key} className="block">
                    <span className="mako-label text-[#7A7F85]">{key.replace(/_/g, " ")}</span>
                    <div className="mt-2.5">
                      {isSimple(value) ? (
                        String(value).length > 90 ? (
                          <textarea
                            className={`${inputCls} min-h-[110px]`}
                            value={value}
                            onChange={(e) => setField(key, e.target.value)}
                          />
                        ) : (
                          <input
                            className={inputCls}
                            value={value}
                            onChange={(e) => setField(key, e.target.value)}
                          />
                        )
                      ) : (
                        <textarea
                          className={`${inputCls} min-h-[130px] font-mono text-xs`}
                          defaultValue={JSON.stringify(value, null, 2)}
                          onBlur={(e) => {
                            try {
                              setField(key, JSON.parse(e.target.value));
                            } catch {
                              toast.error(`Invalid JSON in ${key}`);
                            }
                          }}
                        />
                      )}
                    </div>
                    {(key.includes("image") || key === "gallery") && (
                      <span className="mt-2 block text-xs text-[#7A7F85]">
                        Paste any image URL to replace this photography.
                      </span>
                    )}
                  </label>
                ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#7A7F85]">Select an item to edit.</p>
        )}
      </div>
    </div>
  );
};
